import React, { useState } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// Firebase imports will be done dynamically

const formSchema = z.object({
  suggestion: z.string().min(1, 'Suggestion is required').max(1000, 'Suggestion must be 1000 characters or less'),
  email: z.string().email('Invalid email address').max(100, 'Email must be 100 characters or less').optional().or(z.literal('')),
});

type FeedbackFormValues = z.infer<typeof formSchema>;

function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      suggestion: '',
      email: '',
    },
  });

  const onSubmit = async (values: FeedbackFormValues) => {
    setIsSubmitting(true);
    try {
      // Dynamically import Firebase modules
      const { Timestamp, addDoc } = await import('firebase/firestore');
      const { feedbackCollection } = await import('../lib/firebase');

      // Create feedback data
      const feedbackData = {
        suggestion: values.suggestion.trim(),
        email: values.email?.trim() || 'Anonymous',
        path: window.location.pathname,
        timestamp: Timestamp.now() // Use dynamically imported Timestamp
      };

      await addDoc(feedbackCollection, feedbackData); // Use dynamically imported addDoc and collection

      toast.success('Thank you for your suggestion!');
      form.reset();
      setIsOpen(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send suggestion. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          className="fixed bottom-4 right-4 p-3 rounded-full shadow-lg transition-colors"
          title="Suggest a tool"
          size="icon"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-background light:bg-background rounded-lg p-6 w-full max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-foreground light:text-foreground">
            Suggest a Tool
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="suggestion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-muted-foreground light:text-muted-foreground mb-1">
                    What tool would you like to see?
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the tool you'd like us to add..."
                      className="w-full h-32 rounded-lg bg-muted light:bg-background border-border light:border-border text-foreground light:text-foreground p-3"
                      maxLength={1000}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-muted-foreground mt-1">
                    {field.value.length}/1000 characters
                  </p>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="block text-sm font-medium text-muted-foreground light:text-muted-foreground mb-1">
                    Your Email (optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="We'll notify you when it's added"
                      className="w-full rounded-lg bg-muted light:bg-background border-border light:border-border text-foreground light:text-foreground"
                      maxLength={100}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
              className="w-full py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="h-5 w-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Send Suggestion
                </>
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackButton;